import { html, css } from 'lit';
import { jhaStyles } from '../../styles/jha-styles.js';
import JhaFormInput from '../jha-form-input-base/JhaFormInput.js';
import '../jha-form-floating-group/jha-form-floating-group.js';

const styles = css`
  :host {
    display: block;
  }
  jha-form-floating-group::before {
    content: '$';
    font-size: 18px;
    position: absolute;
    top: var(--jha-currency-input-dollar-top, 35px);
    bottom: var(--jha-currency-input-dollar-bottom, auto);
    left: 15px;
    transform: translateY(-50%);
    color: var(--jha-text-light);
    display: block;
    z-index: 1;
  }
  :host([disabled]) jha-form-floating-group::before {
    opacity: 0.7;
  }
  :host([filled]) {
    --jha-currency-input-dollar-top: 44px;
  }
  :host([outline]) {
    --jha-currency-input-dollar-top: 40px;
  }
  :host([small]) {
    --jha-currency-input-dollar-top: 32px;
  }
  :host([small]) jha-form-floating-group::before {
    bottom: var(
      --jha-currency-input-small-dollar-bottom,
      var(--jha-currency-input-dollar-bottom, auto)
    );
  }
  input {
    text-align: right;
  }

  :host([left]) input {
    text-align: left;
    padding-left: 32px;
  }

  input[type='number']::-webkit-inner-spin-button,
  input[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  /* People-specific styles */
  /* JR: these have all been replaced by css variables (see :host),
   but leaving this here for a reference when the input breaks in People.
  */
  /*
  :host([is-in-people]) #currency-container {
    width: 150px;
  }
  :host([is-in-people]) input {
    color: var(--jha-text-base);
    background: transparent;
    border: 1px solid var(--jha-border-color);
    border-radius: 3px;
  }
  :host([is-in-people]) input:focus {
    border: 1px solid var(--jha-color-primary);
  }
  */

  /* CSS to prevent zooming on iOS when input is focused */
  @supports (-webkit-overflow-scrolling: touch) {
    :host([floating]) input {
      font-size: 16px;
    }
  }
`;

/**
 * @element jha-form-currency-input
 *
 * Extends Jha-form-input see: forms/jha-form-input-base/jhaFormInput.js for list of properties
 */
export default class JhaFormCurrencyInput extends JhaFormInput {
  static get styles() {
    return [jhaStyles, styles];
  }

  static get properties() {
    return {
      min: {
        type: Number,
      },
      max: {
        type: Number,
      },
      placeholder: {
        type: String,
      },
      noLabel: {
        type: Boolean,
      },
      noDecimal: {
        type: Boolean,
      },
      allowNegative: {
        type: Boolean,
      },
      outline: {
        type: Boolean,
        reflect: true,
      },
      alwaysFloat: {
        type: Boolean,
        reflect: true,
      },
      validator: {
        type: Function,
        attribute: false,
      },
      step: {
        type: String,
      },
      hideRequiredIndicator: {
        type: Boolean,
      },
      useCommas: {
        type: Boolean,
      },
      allowedCharacters: {
        type: String,
      },
    };
  }

  constructor() {
    super();
    this.step = '0.01';
    this.amount = '';
    this.min = 0;
    this.max = Infinity;
    this.placeholder = '0.00';
    this.noLabel = false;
    this.noDecimal = false;
    this.outline = false;
    this.small = false;
    this.validator = null;
    this.allowNegative = false;
    this.hideRequiredIndicator = false;
    this.useCommas = false;
    this.allowedCharacters = '[0-9.-]+';
  }

  /**
   * @description getters and setters for amount for backward compatibility
   *
   */
  set amount(newVal) {
    this.value_ = newVal;
  }

  get amount() {
    return this.value_;
  }

  /** @override */
  render() {
    return html`
      <jha-form-floating-group
        ?outline=${this.outline}
        ?small=${this.small}
        always-float
        ?filled=${this.filled}
        ?no-label=${this.noLabel}
        .assistiveText=${this.assistiveText}
        .error=${this.showWarning_() ? this.warning : null}
      >
        <input
          id=${this.id}
          aria-label=${this.label}
          ?readonly=${this.readonly}
          type="text"
          inputmode="decimal"
          .value=${this.formattedValue}
          ?disabled=${this.disabled}
          ?required=${this.required}
          placeholder=${this.placeholder}
          @keypress=${this.onKeypress_}
          @keyup=${this.onInput_}
          @change=${this.onChange_}
          @blur=${this.onBlur_}
          @keydown=${this.onKeydown_}
          class="${this.showWarning_() ? 'warning' : ''}"
          autocomplete="off"
        />
        ${this.label
          ? html`
              <label for=${this.id} class="label"
                >${this.required
                  ? `${this.label} ${this.hideRequiredIndicator ? '' : '*'}`
                  : this.label}</label
              >
            `
          : ''}
      </jha-form-floating-group>
    `;
  }

  /**
   * @description re-formats amount to 2 decimal places (or '')
   * @param {string} value
   */
  formatValue(value) {
    if (value) {
      const amount = parseFloat(String(value).replace(/,/g, ''));
      // eslint-disable-next-line no-nested-ternary
      const val = Number.isNaN(amount)
        ? ''
        : this.allowNegative
        ? amount.toFixed(2)
        : Math.abs(amount).toFixed(2);

      if (this.noDecimal) {
        this.value_ = String(parseInt(val, 10));
      } else {
        this.value_ = val;
      }
    }
  }

  get formattedValue() {
    return this.useCommas
      ? this.value_
          .toString()
          .replace(/,/g, '')
          .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      : this.value_;
  }

  get notInRange() {
    const floatVal = parseFloat(this.value);

    const belowMin = this.min && floatVal < this.min;
    const aboveMax = this.max && floatVal > this.max;

    return belowMin || aboveMax;
  }

  checkValidity() {
    const { value } = this;
    if (this.validator) {
      this.validator(value);
      // Allow formatter to fix stepMismatch
    } else if (this.input_?.validity.stepMismatch) {
      return true;
    } else if (this.notInRange) {
      return false;
    }

    return super.checkValidity();
  }

  /**
   * For browsers that allow commas when entering a value into an `<input type="number">`, but sets its `value`
   * to an empsty string and doesn't recognize the input as a valid value (i.e. Edge/Firefox), this simply
   * listens for comma keydown and ignores them
   * @param {!KeyboardEvent} evt
   */
  onKeydown_(evt) {
    if (evt.code === 'Comma') {
      evt.preventDefault();
    }
  }
}

export { styles as jhaFormCurrencyInputStyles };
